import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {supplierEntityDetailUrl, supplierEntityEditInfoUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";

export type UpdateSupplierInfoVariables = {
    entityId: number;
    /** The "Supplier description" blurb — written to `summary` since the 2026-10-05 mapping fix (§2.1, §3.3). */
    summary: string;
};

export function useUpdateSupplierInfoMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierEntity, DefaultError, UpdateSupplierInfoVariables>({
        mutationFn: async ({entityId, summary}) => {
            const {data} = await queryHttp.patch<SupplierEntity>(
                supplierEntityEditInfoUrl(entityId),
                {summary},
            );
            return data;
        },
        onSuccess: (updated) => {
            queryClient.setQueryData([supplierEntityDetailUrl(updated.id)], updated);
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('supplier_360/entity');
                },
            });
            notificationStore.show('Supplier information updated.', {
                severity: 'success',
                id: `supplier-info-success-${updated.id}`,
                autoHideDuration: 3000,
            });
        },
    });
}
